import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { hhmmss, initials } from '../lib/format';

/**
 * Si algo revienta al pintar, se enseña el error.
 *
 * Sin esto, React desmonta el árbol y queda una pantalla negra: en un taller
 * eso es indistinguible de «el ordenador se ha colgado», y nadie sabe qué
 * contar cuando llama.
 */
export class LimiteDeError extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Fallo al pintar la pantalla', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="app">
        <div className="content" style={{ display: 'grid', placeItems: 'center' }}>
          <div className="card stack" style={{ maxWidth: 620 }}>
            <div className="eyebrow">Error en el terminal</div>
            <h2 className="h2">No se ha podido pintar esta pantalla</h2>
            <p className="muted small" style={{ margin: 0 }}>
              Lo que estuvieras haciendo no se ha perdido: los fichajes se guardan al pulsar, no al pintar.
              Vuelve a cargar la página; si se repite, enseña este texto a quien dé soporte.
            </p>
            <pre
              className="small mono"
              style={{ whiteSpace: 'pre-wrap', color: 'var(--danger)', background: 'var(--surface-2)', padding: 14, borderRadius: 12, margin: 0 }}
            >
              {this.state.error.message}
            </pre>
            <button className="btn btn--primary btn--lg" onClick={() => location.reload()}>
              Recargar
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export function Avatar({ name, small }: { name: string; small?: boolean }) {
  return <div className={small ? 'avatar avatar--sm' : 'avatar'}>{initials(name)}</div>;
}

export function ActionButton({
  label,
  hint,
  tone = 'default',
  disabled,
  onClick,
}: {
  label: string;
  hint?: string;
  tone?: 'default' | 'accent' | 'ok' | 'warn' | 'danger';
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button className={`action${tone === 'default' ? '' : ` action--${tone}`}`} disabled={disabled} onClick={onClick}>
      <span className="action__label">{label}</span>
      {hint ? <span className="action__hint">{hint}</span> : null}
    </button>
  );
}

export function Dialog({
  title,
  subtitle,
  children,
  onClose,
  wide,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose?: () => void;
  wide?: boolean;
}) {
  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={wide ? 'dialog dialog--wide' : 'dialog'}>
        <h2 className="dialog__title">{title}</h2>
        {subtitle ? <p className="muted small" style={{ marginTop: 0 }}>{subtitle}</p> : null}
        {children}
      </div>
    </div>
  );
}

/** Cronómetro que cuenta desde `startedAt` sin pedir nada al servidor. */
export function LiveTimer({ startedAt, baseSeconds = 0, className = '' }: { startedAt: string | null; baseSeconds?: number; className?: string }) {
  const [seconds, setSeconds] = useState(baseSeconds);

  useEffect(() => {
    if (!startedAt) {
      setSeconds(baseSeconds);
      return;
    }
    const start = new Date(startedAt).getTime();
    const update = () => setSeconds(baseSeconds + Math.max(0, Math.round((Date.now() - start) / 1000)));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [startedAt, baseSeconds]);

  return <span className={`mono ${className}`}>{hhmmss(seconds)}</span>;
}

export function Pill({ tone = 'default', children }: { tone?: 'default' | 'ok' | 'warn' | 'danger' | 'accent'; children: ReactNode }) {
  return <span className={`pill${tone === 'default' ? '' : ` pill--${tone}`}`}>{children}</span>;
}

export function Dot({ tone }: { tone: 'ok' | 'warn' | 'danger' | 'live' | 'muted' }) {
  return <span className={`dot${tone === 'muted' ? '' : ` dot--${tone}`}`} />;
}

export function Empty({ icon, title, body }: { icon: string; title: string; body?: string }) {
  return (
    <div className="empty">
      <div className="empty__icon">{icon}</div>
      <div className="h2">{title}</div>
      {body ? <p className="muted">{body}</p> : null}
    </div>
  );
}

/**
 * Un valor pensado para copiarse: PIN, contraseña, nº de documento.
 *
 * Con botón, porque en un diálogo que se cierra al pulsar fuera, seleccionar a
 * mano es fácil de fallar. El texto además se puede seleccionar de un clic.
 */
export function Copiable({
  valor,
  etiqueta,
  grande,
}: {
  valor: string;
  etiqueta?: string;
  grande?: boolean;
}) {
  const [copiado, setCopiado] = useState(false);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sin permiso de portapapeles no se puede copiar solo, pero el valor se
      // sigue viendo y se puede seleccionar a mano.
      setCopiado(false);
    }
  };

  return (
    <div className="stack" style={{ gap: 6 }}>
      {etiqueta ? <div className="small muted">{etiqueta}</div> : null}
      <div className="copiable">
        <span
          className={`copiable__valor mono${grande ? '' : ' h2'}`}
          style={grande ? { fontSize: 46, fontWeight: 600, letterSpacing: '0.12em' } : undefined}
        >
          {valor}
        </span>
        <button className="btn" onClick={() => void copiar()} title="Copiar al portapapeles">
          {copiado ? '✓ Copiado' : 'Copiar'}
        </button>
      </div>
    </div>
  );
}

/**
 * Cambiar la contraseña de alguien: o se escribe una, o se genera.
 *
 * Se ofrecen las dos porque el caso real es distinto según quién sea: a un
 * responsable se le manda la generada, y a quien hay que dictarle la clave por
 * teléfono se le pone una que pueda repetir sin equivocarse.
 */
export function CambiarContrasena({
  usuario,
  onGuardar,
  onCancelar,
}: {
  usuario: string;
  onGuardar: (password?: string) => void;
  onCancelar: () => void;
}) {
  const [password, setPassword] = useState('');
  const corta = password.length > 0 && password.length < 8;

  return (
    <div className="card card--flat stack">
      <div className="eyebrow">Contraseña de {usuario}</div>
      <Field label="Escríbela tú (mínimo 8 caracteres)">
        <input
          className="input"
          autoFocus
          value={password}
          placeholder="déjalo vacío para generar una"
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !corta && password && onGuardar(password)}
        />
      </Field>
      {corta ? <div className="small" style={{ color: 'var(--danger)' }}>Al menos 8 caracteres.</div> : null}
      <div className="row row--wrap">
        <button className="btn" onClick={onCancelar}>Cancelar</button>
        <div className="spacer" />
        <button className="btn" onClick={() => onGuardar(undefined)}>🎲 Generar una</button>
        <button className="btn btn--primary" disabled={password.length < 8} onClick={() => onGuardar(password)}>
          Guardar esta
        </button>
      </div>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
    </label>
  );
}
